import { useEffect, useRef, useState } from "react";
import { ChevronDown, LoaderCircle, Search, Tag, X } from "lucide-react";
import shopApi from "../../../../api/shopApi";
import zaloApi from "../../../../api/zaloApi";
import { useNotification } from "../../../../hooks/useNotification";

export default function ShareMessageModal({
  isOpen,
  setIsOpen,
  shop,
  message,
  tagZalos = [],
}) {
  const { addNotification } = useNotification();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("friends"); // "groups" | "friends"
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const searchRef = useRef(null);
  const tagDropdownRef = useRef(null);

  const MAX_CONTACTS = 50;
  const DEFAULT_GROUP_AVATAR =
    "https://w7.pngwing.com/pngs/924/119/png-transparent-basic-group-team-people-avatar-profile-face-office-icon.png";

  // Helper to get image dimensions
  const getImageInfo = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = url;
    });
  };

  // Fetch contacts (friends and groups) when modal opens
  useEffect(() => {
    if (isOpen && shop?._id) {
      setIsLoadingContacts(true);
      shopApi
        .getContactsFiltered(shop._id, { isFriend: true, isGroup: true })
        .then((res) => {
          if (res.status === 1) {
            setAllContacts(res.data);
          }
          setIsLoadingContacts(false);
        })
        .catch(() => {
          setIsLoadingContacts(false);
        });
    }
  }, [isOpen, shop?._id]);

  // Close tag dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target)
      ) {
        setIsTagDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter contacts based on tab, search, and tag
  const filteredContacts = allContacts.filter((contact) => {
    // Tab filter
    if (activeTab === "groups" && !contact.isGroup) return false;
    if (activeTab === "friends" && contact.isGroup) return false;

    // Search filter
    if (
      searchQuery &&
      !contact.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;

    // Tag filter (match any selected tag)
    if (selectedTagIds.length > 0) {
      const contactInAnyTag = selectedTagIds.some((tagId) => {
        const tag = tagZalos.find((t) => t._id === tagId);
        return tag?.conversations?.includes(contact.uid);
      });
      if (!contactInAnyTag) return false;
    }

    return true;
  });

  // Group contacts alphabetically
  const groupedContacts = filteredContacts.reduce((groups, contact) => {
    const firstLetter = contact.name.charAt(0).toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(contact);
    return groups;
  }, {});

  const sortedLetters = Object.keys(groupedContacts).sort();

  const handleSelectContact = (contact) => {
    if (selectedContacts.find((c) => c._id === contact._id)) {
      setSelectedContacts(
        selectedContacts.filter((c) => c._id !== contact._id)
      );
    } else if (selectedContacts.length < MAX_CONTACTS) {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const handleRemoveContact = (contactId) => {
    setSelectedContacts(selectedContacts.filter((c) => c._id !== contactId));
  };

  const handleClearAll = () => {
    setSelectedContacts([]);
  };

  const handleCancelClick = () => {
    setSearchQuery("");
    setSelectedContacts([]);
    setAdditionalMessage("");
    setSelectedTagIds([]);
    setIsOpen(false);
  };

  const handleShareClick = async () => {
    if (selectedContacts.length === 0) return;

    try {
      setIsLoading(true);
      let hasError = false;

      // Determine what content to share
      const hasImage = message?.type === "image" && message?.img;
      const hasImages = message?.type === "images" && message?.imgs?.length > 0;
      const hasFile = message?.type === "file" && message?.file;
      const hasText = message?.content || additionalMessage;

      // Share single image (fetch once, send to all contacts)
      if (hasImage) {
        try {
          const imgInfo = await getImageInfo(message.img);
          const response = await fetch(message.img);
          const blob = await response.blob();
          const imageFile = new File([blob], "shared_image.jpg", {
            type: blob.type,
          });

          for (const contact of selectedContacts) {
            try {
              const res = await shopApi.sendImage({
                shopId: shop._id,
                uid: contact.uid,
                isGroup: contact.isGroup,
                file: imageFile,
                width: imgInfo.width,
                height: imgInfo.height,
              });

              if (res.status !== 1) {
                hasError = true;
                addNotification(
                  `Lỗi gửi ảnh cho ${contact.name}: ${res.message}`,
                  -1
                );
              }
            } catch (err) {
              hasError = true;
              addNotification(`Lỗi gửi ảnh cho ${contact.name}`, -1);
            }
          }
        } catch (err) {
          hasError = true;
          addNotification("Lỗi tải ảnh để chia sẻ", -1);
        }
      }

      // Share multiple images (fetch all once, send to all contacts)
      if (hasImages) {
        try {
          // Fetch all images with their dimensions first
          const imageData = await Promise.all(
            message.imgs.map(async (imgUrl) => {
              const imgInfo = await getImageInfo(imgUrl);
              const response = await fetch(imgUrl);
              const blob = await response.blob();
              return {
                file: new File([blob], "shared_image.jpg", { type: blob.type }),
                width: imgInfo.width,
                height: imgInfo.height,
              };
            })
          );

          for (const contact of selectedContacts) {
            for (const imgData of imageData) {
              try {
                const res = await shopApi.sendImage({
                  shopId: shop._id,
                  uid: contact.uid,
                  isGroup: contact.isGroup,
                  file: imgData.file,
                  width: imgData.width,
                  height: imgData.height,
                });

                if (res.status !== 1) {
                  hasError = true;
                  addNotification(
                    `Lỗi gửi ảnh cho ${contact.name}: ${res.message}`,
                    -1
                  );
                }
              } catch (err) {
                hasError = true;
                addNotification(`Lỗi gửi ảnh cho ${contact.name}`, -1);
              }
            }
          }
        } catch (err) {
          hasError = true;
          addNotification("Lỗi tải ảnh để chia sẻ", -1);
        }
      }

      // Share files (fetch once, send to all contacts)
      if (hasFile) {
        try {
          const response = await fetch(message.file);
          const blob = await response.blob();
          const fileName = message.content || "shared_file";
          const fileToShare = new File([blob], fileName, { type: blob.type });

          for (const contact of selectedContacts) {
            try {
              const res = await zaloApi.sendFile({
                shopId: shop._id,
                id: contact.uid,
                isGroup: contact.isGroup,
                file: fileToShare,
              });

              if (res.status !== 1) {
                hasError = true;
                addNotification(
                  `Lỗi gửi file cho ${contact.name}: ${res.message}`,
                  -1
                );
              }
            } catch (err) {
              hasError = true;
              addNotification(`Lỗi gửi file cho ${contact.name}`, -1);
            }
          }
        } catch (err) {
          hasError = true;
          addNotification("Lỗi tải file để chia sẻ", -1);
        }
      }

      // Share text content (batch for non-group, or use shareMessage)
      if (hasText && !hasImage && !hasImages && !hasFile) {
        const textContent = additionalMessage || message?.content;

        // Separate groups and friends
        const groupContacts = selectedContacts.filter((c) => c.isGroup);
        const friendContacts = selectedContacts.filter((c) => !c.isGroup);

        // Share to friends using shareMessage API
        if (friendContacts.length > 0) {
          try {
            const res = await shopApi.shareMessage({
              shopId: shop._id,
              uids: friendContacts.map((c) => c.uid),
              content: textContent,
              isGroup: false,
            });

            if (res.status !== 1) {
              hasError = true;
              addNotification(`Lỗi chia sẻ tin nhắn: ${res.message}`, -1);
            }
          } catch (err) {
            hasError = true;
            addNotification("Lỗi chia sẻ tin nhắn", -1);
          }
        }

        // Share to groups using shareMessage API
        if (groupContacts.length > 0) {
          try {
            const res = await shopApi.shareMessage({
              shopId: shop._id,
              uids: groupContacts.map((c) => c.uid),
              content: textContent,
              isGroup: true,
            });

            if (res.status !== 1) {
              hasError = true;
              addNotification(`Lỗi chia sẻ tin nhắn nhóm: ${res.message}`, -1);
            }
          } catch (err) {
            hasError = true;
            addNotification("Lỗi chia sẻ tin nhắn nhóm", -1);
          }
        }
      }

      // If has additional message after image/file
      if ((hasImage || hasImages || hasFile) && additionalMessage) {
        const groupContacts = selectedContacts.filter((c) => c.isGroup);
        const friendContacts = selectedContacts.filter((c) => !c.isGroup);

        if (friendContacts.length > 0) {
          try {
            const res = await shopApi.shareMessage({
              shopId: shop._id,
              uids: friendContacts.map((c) => c.uid),
              content: additionalMessage,
              isGroup: false,
            });
            if (res.status !== 1) hasError = true;
          } catch {
            hasError = true;
          }
        }

        if (groupContacts.length > 0) {
          try {
            const res = await shopApi.shareMessage({
              shopId: shop._id,
              uids: groupContacts.map((c) => c.uid),
              content: additionalMessage,
              isGroup: true,
            });
            if (res.status !== 1) hasError = true;
          } catch {
            hasError = true;
          }
        }
      }

      setIsLoading(false);

      if (!hasError) {
        addNotification("Chia sẻ thành công", 1);
      }
      // Always close modal after share attempt
      handleCancelClick();
    } catch (error) {
      setIsLoading(false);
      addNotification(error.message || "Lỗi chia sẻ", -1);
      handleCancelClick();
    }
  };

  // Get preview text
  const getPreviewText = () => {
    if (message?.type === "image") return "Chia sẻ ảnh";
    if (message?.type === "images")
      return `Chia sẻ ${message.imgs?.length || 0} ảnh`;
    if (message?.type === "file") return "Chia sẻ file";
    if (message?.type === "video") return "Chia sẻ video";
    if (message?.content) return `Chia sẻ tin nhắn: ${message.content}`;
    return "Chia sẻ";
  };

  useEffect(() => {
    if (isOpen) {
      searchRef?.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 text-black z-30">
      <div className="bg-white w-full max-w-2xl h-[90vh] rounded-lg shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">Chia sẻ</h2>
          <button
            onClick={handleCancelClick}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Tìm kiếm..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              ref={searchRef}
            />
          </div>
        </div>

        {/* Tabs and Filter */}
        <div className="flex items-center justify-between px-4 pb-2">
          <div className="flex gap-4">
            <button
              className={`pb-1 ${
                activeTab === "friends"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("friends")}
            >
              Bạn bè
            </button>
            <button
              className={`pb-1 ${
                activeTab === "groups"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("groups")}
            >
              Nhóm trò chuyện
            </button>
          </div>

          {/* Tag Filter Dropdown */}
          <div className="relative" ref={tagDropdownRef}>
            <button
              className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
              onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
            >
              Phân loại
              <ChevronDown className="w-4 h-4" />
            </button>

            {isTagDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-48 max-h-72 overflow-y-auto z-20">
                <div
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setSelectedTagIds([]);
                  }}
                >
                  <span
                    className={selectedTagIds.length === 0 ? "font-medium" : ""}
                  >
                    Tất cả
                  </span>
                </div>
                {tagZalos.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag._id);
                  return (
                    <div
                      key={tag._id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedTagIds(
                            selectedTagIds.filter((id) => id !== tag._id)
                          );
                        } else {
                          setSelectedTagIds([...selectedTagIds, tag._id]);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-4 h-4 rounded text-blue-600"
                      />
                      <Tag
                        className="w-4 h-4"
                        fill={tag.color || "#ccc"}
                        stroke={tag.color || "#ccc"}
                      />
                      <span className={isSelected ? "font-medium" : ""}>
                        {tag.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden border-t">
          {/* Left: Contact list */}
          <div className="flex-1 overflow-y-auto">
            {isLoadingContacts ? (
              <div className="flex items-center justify-center p-8">
                <LoaderCircle
                  className="animate-spin text-blue-600"
                  width={32}
                  height={32}
                />
              </div>
            ) : sortedLetters.length === 0 ? (
              <div className="text-center text-gray-500 p-8">
                Không tìm thấy
              </div>
            ) : (
              sortedLetters.map((letter) => (
                <div key={letter}>
                  <div className="px-4 py-1 text-sm font-medium text-gray-500 bg-gray-50">
                    {letter}
                  </div>
                  {groupedContacts[letter].map((contact) => (
                    <div
                      key={contact._id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleSelectContact(contact)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContacts.some(
                          (c) => c._id === contact._id
                        )}
                        onChange={() => handleSelectContact(contact)}
                        className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                        disabled={
                          !selectedContacts.some(
                            (c) => c._id === contact._id
                          ) && selectedContacts.length >= MAX_CONTACTS
                        }
                      />
                      <img
                        src={
                          contact.avatar ||
                          (contact.isGroup
                            ? DEFAULT_GROUP_AVATAR
                            : contact.avatar)
                        }
                        alt={contact.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="truncate">{contact.name}</span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Right: Selected contacts */}
          <div className="w-56 border-l p-3 flex flex-col overflow-hidden bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                Đã chọn: {selectedContacts.length}/{MAX_CONTACTS}
              </span>
              {selectedContacts.length > 0 && (
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={handleClearAll}
                >
                  Xóa
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {selectedContacts.map((contact) => (
                <div
                  key={contact._id}
                  className="flex items-center gap-2 bg-white rounded-lg p-2"
                >
                  <img
                    src={
                      contact.avatar ||
                      (contact.isGroup ? DEFAULT_GROUP_AVATAR : contact.avatar)
                    }
                    alt={contact.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="flex-1 text-sm truncate">
                    {contact.name}
                  </span>
                  <button
                    onClick={() => handleRemoveContact(contact._id)}
                    className="p-0.5 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="px-4 py-3 border-t bg-gray-50">
          <div className="text-sm text-gray-600">{getPreviewText()}</div>
        </div>

        {/* Message input */}
        <div className="px-4 py-2 border-t">
          <input
            type="text"
            placeholder="Nhập tin nhắn..."
            value={additionalMessage}
            onChange={(e) => setAdditionalMessage(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={handleCancelClick}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Hủy
          </button>
          <button
            onClick={handleShareClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
            disabled={selectedContacts.length === 0 || isLoading}
          >
            {isLoading && (
              <LoaderCircle
                className="animate-spin text-white mr-2"
                width={20}
                height={20}
              />
            )}
            Chia sẻ
          </button>
        </div>
      </div>
    </div>
  );
}
